import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, CreditCard, Palette, Check, Link, Upload, Edit2, Trash2, RefreshCw, Landmark } from 'lucide-react';
import api from '../../services/api';
import './CardArtPicker.css';

// Predefined color themes for cards
// isDark: true means we need to make the logo white (if it's a simple logo)
const CARD_THEMES = [
  { id: 'midnight', name: 'Midnight', colors: ['#1a1a2e', '#16213e', '#0f3460'], textColor: '#ffffff', isDark: true },
  { id: 'ocean', name: 'Ocean', colors: ['#0077b6', '#00b4d8', '#90e0ef'], textColor: '#ffffff', isDark: true },
  { id: 'sunset', name: 'Sunset', colors: ['#f72585', '#b5179e', '#7209b7'], textColor: '#ffffff', isDark: true },
  { id: 'forest', name: 'Forest', colors: ['#2d6a4f', '#40916c', '#52b788'], textColor: '#ffffff', isDark: true },
  { id: 'slate', name: 'Slate', colors: ['#374151', '#4b5563', '#6b7280'], textColor: '#ffffff', isDark: true },
  { id: 'rose', name: 'Rose Gold', colors: ['#b76e79', '#c9a0a0', '#e8c4c4'], textColor: '#4a3333', isDark: false },
  { id: 'gold', name: 'Gold', colors: ['#b8860b', '#daa520', '#f4c542'], textColor: '#3d2e00', isDark: false },
  { id: 'platinum', name: 'Platinum', colors: ['#8e9eab', '#c4c4c4', '#eef2f3'], textColor: '#333333', isDark: false },
  { id: 'ember', name: 'Ember', colors: ['#d62828', '#f77f00', '#fcbf49'], textColor: '#ffffff', isDark: true },
  { id: 'aurora', name: 'Aurora', colors: ['#12c2e9', '#c471ed', '#f64f59'], textColor: '#ffffff', isDark: true },
  { id: 'charcoal', name: 'Charcoal', colors: ['#232526', '#414345', '#232526'], textColor: '#ffffff', isDark: true },
  { id: 'sapphire', name: 'Sapphire', colors: ['#0f2027', '#203a43', '#2c5364'], textColor: '#ffffff', isDark: true },
];

// Domain mapping for common financial institutions
const DOMAIN_MAP = {
  'chase': 'chase.com',
  'bank of america': 'bankofamerica.com',
  'bofa': 'bankofamerica.com',
  'wells fargo': 'wellsfargo.com',
  'citi': 'citi.com',
  'citibank': 'citi.com',
  'capital one': 'capitalone.com',
  'discover': 'discover.com',
  'american express': 'americanexpress.com',
  'amex': 'americanexpress.com',
  'us bank': 'usbank.com',
  'pnc': 'pnc.com',
  'td bank': 'td.com',
  'navy federal': 'navyfederal.org',
  'usaa': 'usaa.com',
  'ally': 'ally.com',
  'synchrony': 'synchrony.com',
  'barclays': 'barclays.com',
  'apple': 'apple.com',
  'paypal': 'paypal.com',
  'venmo': 'venmo.com',
  'marcus': 'marcus.com',
  'sofi': 'sofi.com',
  'chime': 'chime.com',
  'amazon': 'amazon.com',
  'costco': 'costco.com',
  'target': 'target.com',
  'walmart': 'walmart.com',
  'fidelity': 'fidelity.com',
  'schwab': 'schwab.com',
  'vanguard': 'vanguard.com',
  'robinhood': 'robinhood.com',
  'first national': 'fnb-online.com',
  'regions': 'regions.com',
  'huntington': 'huntington.com',
  'fifth third': '53.com',
  'citizens': 'citizensbank.com',
  'key bank': 'key.com',
  'truist': 'truist.com',
  'santander': 'santander.com',
  'bmo': 'bmo.com',
};

/**
 * Guess domain from institution name
 */
function guessDomain(name) {
  const normalized = name.toLowerCase().trim();
  
  for (const [key, domain] of Object.entries(DOMAIN_MAP)) {
    if (normalized.includes(key)) {
      return domain;
    }
  }
  
  // Fallback: convert name to domain-like format
  return normalized.replace(/[^a-z0-9]/g, '') + '.com';
}

/**
 * Check if a logo is "simple" (mostly transparent background)
 * Simple logos can be converted to white, complex logos (with backgrounds) should not
 * Returns true if more than 30% of pixels are transparent
 */
function isSimpleLogo(img) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  canvas.width = img.width;
  canvas.height = img.height;
  
  ctx.drawImage(img, 0, 0);
  
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  
  let transparentPixels = 0;
  let totalPixels = data.length / 4;
  
  for (let i = 0; i < data.length; i += 4) {
    const alpha = data[i + 3];
    if (alpha < 128) { // Consider semi-transparent as transparent
      transparentPixels++;
    }
  }
  
  const transparentRatio = transparentPixels / totalPixels;
  // If more than 30% is transparent, it's a simple logo (icon on transparent bg)
  return transparentRatio > 0.3;
}

/**
 * Convert a logo to white by manipulating pixel data
 * Keeps the alpha channel intact
 */
function makeLogoWhite(img) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  canvas.width = img.width;
  canvas.height = img.height;
  
  // Draw original image
  ctx.drawImage(img, 0, 0);
  
  // Get image data
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  
  // Convert all non-transparent pixels to white
  for (let i = 0; i < data.length; i += 4) {
    const alpha = data[i + 3];
    if (alpha > 0) {
      data[i] = 255;     // R
      data[i + 1] = 255; // G
      data[i + 2] = 255; // B
      // Keep alpha as is
    }
  }
  
  ctx.putImageData(imageData, 0, 0);
  return canvas;
}

/**
 * Draw contactless payment icon
 */
function drawContactlessIcon(ctx, x, y, size, color) {
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'round';
  
  // Draw the wifi-like arcs
  for (let i = 0; i < 4; i++) {
    ctx.beginPath();
    const radius = (size / 4) * (i + 1);
    ctx.arc(x, y + size/2, radius, -Math.PI * 0.7, -Math.PI * 0.3);
    ctx.stroke();
  }
}

/**
 * Draw a bank/savings account card (no chip, no contactless)
 */
function drawBankAccountCard(ctx, width, height, theme, institutionName, lastFour, logoImg, logoIsSimple) {
  // Draw gradient background
  const gradient = ctx.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, theme.colors[0]);
  gradient.addColorStop(0.5, theme.colors[1]);
  gradient.addColorStop(1, theme.colors[2]);
  
  // Rounded rectangle
  const radius = 16;
  ctx.beginPath();
  ctx.moveTo(radius, 0);
  ctx.lineTo(width - radius, 0);
  ctx.quadraticCurveTo(width, 0, width, radius);
  ctx.lineTo(width, height - radius);
  ctx.quadraticCurveTo(width, height, width - radius, height);
  ctx.lineTo(radius, height);
  ctx.quadraticCurveTo(0, height, 0, height - radius);
  ctx.lineTo(0, radius);
  ctx.quadraticCurveTo(0, 0, radius, 0);
  ctx.closePath();
  ctx.fillStyle = gradient;
  ctx.fill();

  // Subtle pattern overlay
  ctx.save();
  ctx.globalAlpha = 0.03;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1;
  for (let i = -height; i < width + height; i += 8) {
    ctx.beginPath();
    ctx.moveTo(i, 0);
    ctx.lineTo(i + height, height);
    ctx.stroke();
  }
  ctx.restore();

  // Decorative circles
  ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
  ctx.beginPath();
  ctx.arc(width - 40, 50, 120, 0, Math.PI * 2);
  ctx.fill();
  
  ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
  ctx.beginPath();
  ctx.arc(60, height + 30, 100, 0, Math.PI * 2);
  ctx.fill();

  // Shine effect
  const shineGradient = ctx.createLinearGradient(0, 0, width, height);
  shineGradient.addColorStop(0, 'rgba(255, 255, 255, 0.15)');
  shineGradient.addColorStop(0.3, 'rgba(255, 255, 255, 0)');
  shineGradient.addColorStop(0.7, 'rgba(255, 255, 255, 0)');
  shineGradient.addColorStop(1, 'rgba(255, 255, 255, 0.1)');
  ctx.fillStyle = shineGradient;
  ctx.fillRect(0, 0, width, height);

  // Draw bank icon (instead of chip)
  const iconX = 24;
  const iconY = 50;
  const iconSize = 40;
  
  ctx.fillStyle = theme.textColor;
  ctx.globalAlpha = 0.9;
  
  // Simple bank building icon
  ctx.beginPath();
  // Roof triangle
  ctx.moveTo(iconX + iconSize/2, iconY);
  ctx.lineTo(iconX + iconSize, iconY + 15);
  ctx.lineTo(iconX, iconY + 15);
  ctx.closePath();
  ctx.fill();
  
  // Pillars
  const pillarWidth = 6;
  const pillarHeight = 20;
  const pillarY = iconY + 17;
  ctx.fillRect(iconX + 5, pillarY, pillarWidth, pillarHeight);
  ctx.fillRect(iconX + 17, pillarY, pillarWidth, pillarHeight);
  ctx.fillRect(iconX + 29, pillarY, pillarWidth, pillarHeight);
  
  // Base
  ctx.fillRect(iconX, iconY + 38, iconSize, 4);
  
  ctx.globalAlpha = 1;

  // Account type label
  ctx.font = '600 10px -apple-system, sans-serif';
  ctx.fillStyle = theme.textColor;
  ctx.globalAlpha = 0.7;
  ctx.fillText('SAVINGS ACCOUNT', iconX + iconSize + 15, iconY + 25);
  ctx.globalAlpha = 1;

  // Account number (bottom)
  ctx.fillStyle = theme.textColor;
  ctx.font = '600 18px "SF Mono", Consolas, monospace';
  const accountNum = lastFour ? `•••••••• ${lastFour}` : '•••••••• ••••';
  ctx.fillText(accountNum, 24, height - 56);

  // Institution name (bottom left)
  ctx.font = '500 13px -apple-system, sans-serif';
  const displayName = institutionName ? institutionName.toUpperCase() : '';
  ctx.fillText(displayName.substring(0, 22), 24, height - 16);

  // Draw logo if available
  if (logoImg) {
    const maxW = 100;
    const maxH = 36;
    let logoW = logoImg.width;
    let logoH = logoImg.height;
    
    const scaleW = maxW / logoW;
    const scaleH = maxH / logoH;
    const scale = Math.min(scaleW, scaleH, 1);
    
    logoW = logoW * scale;
    logoH = logoH * scale;
    
    const logoX = width - logoW - 20;
    const logoY = 18;
    
    const shouldWhiten = theme.isDark && logoIsSimple;
    
    if (shouldWhiten) {
      const whiteLogo = makeLogoWhite(logoImg);
      ctx.drawImage(whiteLogo, logoX, logoY, logoW, logoH);
    } else {
      ctx.drawImage(logoImg, logoX, logoY, logoW, logoH);
    }
  }
}

/**
 * CardArtPicker - Modal for creating/selecting card art
 * Three modes: Generate (custom card), Upload (file), URL (paste link)
 */
function CardArtPicker({ 
  isOpen, 
  onClose, 
  onSelect, 
  currentImage, 
  institutionHint = '',
  lastFourHint = '',
  nameOnCardHint = '',
  accountType = 'credit_card'
}) {
  const [mode, setMode] = useState('generate');
  const [selectedTheme, setSelectedTheme] = useState('midnight');
  const [customUrl, setCustomUrl] = useState('');
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [logoIsSimple, setLogoIsSimple] = useState(true);
  const [logoLoading, setLogoLoading] = useState(false);
  const [urlError, setUrlError] = useState('');
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  // Determine if this is a card-less account type (savings, other)
  const isBankAccount = accountType === 'savings' || accountType === 'other';

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setMode('generate');
      setSelectedTheme('midnight');
      setCustomUrl('');
      setUrlError('');
      setLogoDataUrl(null);
      setLogoIsSimple(true);
      setUploadPreview(null);
      setUploadError('');
      
      if (institutionHint) {
        fetchLogo(institutionHint);
      }
    }
  }, [isOpen, institutionHint]);

  // Fetch logo via backend proxy
  const fetchLogo = async (institutionName) => {
    if (!institutionName || institutionName.trim().length < 2) {
      setLogoDataUrl(null);
      return;
    }

    setLogoLoading(true);
    const domain = guessDomain(institutionName);
    
    try {
      const response = await api.get(`/proxy/logo?domain=${encodeURIComponent(domain)}`);
      if (response.data.success && response.data.dataUrl) {
        setLogoDataUrl(response.data.dataUrl);
        
        // Check if logo is simple (can be whitened)
        const img = new Image();
        img.onload = () => {
          setLogoIsSimple(isSimpleLogo(img));
        };
        img.src = response.data.dataUrl;
      } else {
        setLogoDataUrl(null);
      }
    } catch (error) {
      console.log('Could not fetch logo:', error.message);
      setLogoDataUrl(null);
    }
    
    setLogoLoading(false);
  };

  // Generate card image using canvas
  const generateCardImage = () => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      const width = 340;
      const height = 214;
      canvas.width = width;
      canvas.height = height;

      const theme = CARD_THEMES.find(t => t.id === selectedTheme) || CARD_THEMES[0];

      // If it's a bank account (savings), draw different design
      if (isBankAccount) {
        const drawCard = () => {
          if (logoDataUrl) {
            const logoImg = new Image();
            logoImg.onload = () => {
              drawBankAccountCard(ctx, width, height, theme, institutionHint, lastFourHint, logoImg, isSimpleLogo(logoImg));
              resolve(canvas.toDataURL('image/png'));
            };
            logoImg.onerror = () => {
              drawBankAccountCard(ctx, width, height, theme, institutionHint, lastFourHint, null, false);
              resolve(canvas.toDataURL('image/png'));
            };
            logoImg.src = logoDataUrl;
          } else {
            drawBankAccountCard(ctx, width, height, theme, institutionHint, lastFourHint, null, false);
            resolve(canvas.toDataURL('image/png'));
          }
        };
        drawCard();
        return;
      }
      
      // Regular credit card design
      // Draw gradient background
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, theme.colors[0]);
      gradient.addColorStop(0.5, theme.colors[1]);
      gradient.addColorStop(1, theme.colors[2]);
      
      // Rounded rectangle
      const radius = 16;
      ctx.beginPath();
      ctx.moveTo(radius, 0);
      ctx.lineTo(width - radius, 0);
      ctx.quadraticCurveTo(width, 0, width, radius);
      ctx.lineTo(width, height - radius);
      ctx.quadraticCurveTo(width, height, width - radius, height);
      ctx.lineTo(radius, height);
      ctx.quadraticCurveTo(0, height, 0, height - radius);
      ctx.lineTo(0, radius);
      ctx.quadraticCurveTo(0, 0, radius, 0);
      ctx.closePath();
      ctx.fillStyle = gradient;
      ctx.fill();

      // Subtle pattern overlay - diagonal lines
      ctx.save();
      ctx.globalAlpha = 0.03;
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1;
      for (let i = -height; i < width + height; i += 8) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i + height, height);
        ctx.stroke();
      }
      ctx.restore();

      // Decorative circles with blur effect
      ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
      ctx.beginPath();
      ctx.arc(width - 40, 50, 120, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
      ctx.beginPath();
      ctx.arc(60, height + 30, 100, 0, Math.PI * 2);
      ctx.fill();

      // Shine effect - diagonal gradient overlay
      const shineGradient = ctx.createLinearGradient(0, 0, width, height);
      shineGradient.addColorStop(0, 'rgba(255, 255, 255, 0.15)');
      shineGradient.addColorStop(0.3, 'rgba(255, 255, 255, 0)');
      shineGradient.addColorStop(0.7, 'rgba(255, 255, 255, 0)');
      shineGradient.addColorStop(1, 'rgba(255, 255, 255, 0.1)');
      ctx.fillStyle = shineGradient;
      ctx.fillRect(0, 0, width, height);

      // Draw chip with more detail
      const chipX = 24;
      const chipY = 55;
      const chipW = 50;
      const chipH = 38;
      
      // Chip base
      const chipGradient = ctx.createLinearGradient(chipX, chipY, chipX + chipW, chipY + chipH);
      chipGradient.addColorStop(0, '#e8d5a3');
      chipGradient.addColorStop(0.3, '#f5e6c3');
      chipGradient.addColorStop(0.5, '#d4bc7c');
      chipGradient.addColorStop(0.7, '#f5e6c3');
      chipGradient.addColorStop(1, '#c9a94e');
      ctx.fillStyle = chipGradient;
      ctx.beginPath();
      ctx.roundRect(chipX, chipY, chipW, chipH, 5);
      ctx.fill();
      
      // Chip border
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.15)';
      ctx.lineWidth = 1;
      ctx.stroke();
      
      // Chip contact pads
      ctx.fillStyle = 'rgba(180, 160, 100, 0.6)';
      ctx.fillRect(chipX + 8, chipY + 6, 14, 10);
      ctx.fillRect(chipX + 28, chipY + 6, 14, 10);
      ctx.fillRect(chipX + 8, chipY + 22, 14, 10);
      ctx.fillRect(chipX + 28, chipY + 22, 14, 10);
      
      // Center divider
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
      ctx.beginPath();
      ctx.moveTo(chipX + chipW/2, chipY + 4);
      ctx.lineTo(chipX + chipW/2, chipY + chipH - 4);
      ctx.stroke();

      // Draw contactless icon
      ctx.globalAlpha = 0.7;
      drawContactlessIcon(ctx, chipX + chipW + 18, chipY + 5, 24, theme.textColor);
      ctx.globalAlpha = 1;

      // Text color
      ctx.fillStyle = theme.textColor;

      // Card number with embossed look
      const cardNumber = lastFourHint 
        ? `•••• •••• •••• ${lastFourHint}`
        : '•••• •••• •••• ••••';
      
      // Shadow for embossed effect
      ctx.save();
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
      ctx.font = '600 20px "SF Mono", Consolas, monospace';
      ctx.fillText(cardNumber, 25, height - 55);
      ctx.restore();
      
      // Main text
      ctx.fillStyle = theme.textColor;
      ctx.font = '600 20px "SF Mono", Consolas, monospace';
      ctx.fillText(cardNumber, 24, height - 56);

      // Name on card (bottom left)
      ctx.font = '500 13px -apple-system, sans-serif';
      const displayName = nameOnCardHint ? nameOnCardHint.toUpperCase() : '';
      ctx.fillText(displayName.substring(0, 22), 24, height - 16);

      // Valid thru section
      ctx.font = '400 8px -apple-system, sans-serif';
      ctx.globalAlpha = 0.6;
      ctx.fillText('VALID', 180, height - 32);
      ctx.fillText('THRU', 180, height - 24);
      ctx.globalAlpha = 1;
      
      ctx.font = '500 13px -apple-system, sans-serif';
      ctx.fillText('••/••', 205, height - 24);

      // Function to finalize
      const finalize = () => {
        resolve(canvas.toDataURL('image/png'));
      };

      // Draw logo if available
      if (logoDataUrl) {
        const logoImg = new Image();
        
        logoImg.onload = () => {
          // Calculate dimensions maintaining aspect ratio
          const maxW = 100;
          const maxH = 36;
          let logoW = logoImg.width;
          let logoH = logoImg.height;
          
          // Scale to fit within bounds
          const scaleW = maxW / logoW;
          const scaleH = maxH / logoH;
          const scale = Math.min(scaleW, scaleH, 1);
          
          logoW = logoW * scale;
          logoH = logoH * scale;
          
          const logoX = width - logoW - 20;
          const logoY = 18;
          
          // Only convert to white if dark theme AND logo is simple (transparent bg)
          const shouldWhiten = theme.isDark && isSimpleLogo(logoImg);
          
          if (shouldWhiten) {
            const whiteLogo = makeLogoWhite(logoImg);
            ctx.drawImage(whiteLogo, logoX, logoY, logoW, logoH);
          } else {
            ctx.drawImage(logoImg, logoX, logoY, logoW, logoH);
          }
          
          finalize();
        };
        
        logoImg.onerror = () => {
          finalize();
        };
        
        logoImg.src = logoDataUrl;
      } else {
        finalize();
      }
    });
  };

  const handleGenerateAndSelect = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    const dataUrl = await generateCardImage();
    onSelect({ type: 'generated', dataUrl });
    // Delay close to prevent click from propagating to parent modal backdrop.
    // requestAnimationFrame (~16ms) is not enough; use setTimeout to ensure
    // the parent modal's closeOnBackdrop guard is active before we remove our overlay.
    setTimeout(() => onClose(), 50);
  };

  const handleUrlSubmit = (e) => {
    e?.stopPropagation();
    e?.preventDefault?.();
    if (!customUrl.trim()) {
      setUrlError('Please enter a URL');
      return;
    }
    
    try {
      new URL(customUrl);
      onSelect({ type: 'custom', url: customUrl });
      setTimeout(() => onClose(), 50);
    } catch {
      setUrlError('Please enter a valid URL');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setUploadPreview(reader.result);
      setUploadError('');
    };
    reader.readAsDataURL(file);
  };

  const handleUploadSubmit = (e) => {
    e?.stopPropagation();
    e?.preventDefault?.();
    if (uploadPreview) {
      onSelect({ type: 'uploaded', dataUrl: uploadPreview });
      setTimeout(() => onClose(), 50);
    }
  };

  const handleRemoveImage = (e) => {
    e?.stopPropagation();
    e?.preventDefault?.();
    onSelect({ type: 'clear' });
    setTimeout(() => onClose(), 50);
  };

  if (!isOpen) return null;

  const currentTheme = CARD_THEMES.find(t => t.id === selectedTheme) || CARD_THEMES[0];
  const gradientStyle = `linear-gradient(135deg, ${currentTheme.colors[0]} 0%, ${currentTheme.colors[1]} 50%, ${currentTheme.colors[2]} 100%)`;
  
  // Only apply white filter if dark theme AND simple logo
  const shouldApplyWhiteFilter = currentTheme.isDark && logoIsSimple;

  const handleOverlayClick = (e) => {
    e.stopPropagation();
    onClose();
  };

  return ReactDOM.createPortal(
    <div className="card-art-picker-overlay" onClick={handleOverlayClick}>
      <div className="card-art-picker-modal" onClick={e => e.stopPropagation()}>
        <div className="card-art-picker-header">
          <h3>
            <Palette size={20} />
            Card Image
          </h3>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="card-art-picker-content">
          {/* Mode Tabs */}
          <div className="picker-mode-tabs">
            <button 
              className={`mode-tab ${mode === 'generate' ? 'active' : ''}`}
              onClick={() => setMode('generate')}
            >
              <CreditCard size={16} />
              Design
            </button>
            <button 
              className={`mode-tab ${mode === 'upload' ? 'active' : ''}`}
              onClick={() => setMode('upload')}
            >
              <Upload size={16} />
              Upload
            </button>
            <button 
              className={`mode-tab ${mode === 'url' ? 'active' : ''}`}
              onClick={() => setMode('url')}
            >
              <Link size={16} />
              URL
            </button>
          </div>

          {mode === 'generate' && (
            <>
              {/* Card Preview */}
              <div className="card-preview-container">
                <div 
                  className="generated-card-preview"
                  style={{ background: gradientStyle }}
                >
                  {/* Shine overlay */}
                  <div className="preview-shine"></div>
                  
                  {/* Pattern overlay */}
                  <div className="preview-pattern"></div>

                  {isBankAccount ? (
                    /* Bank/Savings account design */
                    <>
                      {/* Bank icon instead of chip */}
                      <div className="preview-bank-icon" style={{ color: currentTheme.textColor }}>
                        <Landmark size={36} />
                        <span className="bank-label">SAVINGS ACCOUNT</span>
                      </div>

                      {/* Logo area */}
                      <div className="preview-logo-area">
                        {logoLoading ? (
                          <RefreshCw size={20} className="spinning" />
                        ) : logoDataUrl ? (
                          <img 
                            src={logoDataUrl} 
                            alt="" 
                            className={`preview-logo ${shouldApplyWhiteFilter ? 'logo-white' : ''}`}
                            onError={() => setLogoDataUrl(null)}
                          />
                        ) : null}
                      </div>

                      {/* Account number */}
                      <div className="preview-card-number preview-account-number" style={{ color: currentTheme.textColor }}>
                        {lastFourHint ? `•••••••• ${lastFourHint}` : '•••••••• ••••'}
                      </div>

                      {/* Institution name at bottom */}
                      <div className="preview-bottom-row" style={{ color: currentTheme.textColor }}>
                        <div className="preview-institution-name">
                          {institutionHint ? institutionHint.toUpperCase().substring(0, 22) : ''}
                        </div>
                      </div>
                    </>
                  ) : (
                    /* Credit card design */
                    <>
                      {/* Chip */}
                      <div className="preview-chip">
                        <div className="chip-contacts">
                          <div className="chip-contact"></div>
                          <div className="chip-contact"></div>
                          <div className="chip-contact"></div>
                          <div className="chip-contact"></div>
                        </div>
                      </div>

                      {/* Contactless icon */}
                      <div className="preview-contactless" style={{ color: currentTheme.textColor }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M6 12a6 6 0 0 1 6-6" />
                          <path d="M6 6a12 12 0 0 1 12 6" />
                          <path d="M6 18a18 18 0 0 1 18-6" />
                        </svg>
                      </div>

                      {/* Logo area */}
                      <div className="preview-logo-area">
                        {logoLoading ? (
                          <RefreshCw size={20} className="spinning" />
                        ) : logoDataUrl ? (
                          <img 
                            src={logoDataUrl} 
                            alt="" 
                            className={`preview-logo ${shouldApplyWhiteFilter ? 'logo-white' : ''}`}
                            onError={() => setLogoDataUrl(null)}
                          />
                        ) : null}
                      </div>

                      {/* Card number */}
                      <div className="preview-card-number" style={{ color: currentTheme.textColor }}>
                        {lastFourHint ? `•••• •••• •••• ${lastFourHint}` : '•••• •••• •••• ••••'}
                      </div>

                      {/* Bottom row - Name on card and valid thru */}
                      <div className="preview-bottom-row" style={{ color: currentTheme.textColor }}>
                        <div className="preview-name-on-card">
                          {nameOnCardHint ? nameOnCardHint.toUpperCase().substring(0, 22) : ''}
                        </div>
                        <div className="preview-valid-thru">
                          <span className="valid-label">VALID<br/>THRU</span>
                          <span className="valid-date">••/••</span>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Decorative elements */}
                  <div className="preview-decoration">
                    <div className="decoration-circle circle-1"></div>
                    <div className="decoration-circle circle-2"></div>
                  </div>
                </div>
              </div>

              {/* Theme Selector */}
              <div className="theme-selector-section">
                <label className="section-label">Choose Color</label>
                <div className="theme-grid">
                  {CARD_THEMES.map(theme => {
                    const tGradient = `linear-gradient(135deg, ${theme.colors[0]} 0%, ${theme.colors[1]} 50%, ${theme.colors[2]} 100%)`;
                    return (
                      <button
                        key={theme.id}
                        className={`theme-swatch ${selectedTheme === theme.id ? 'selected' : ''}`}
                        style={{ background: tGradient }}
                        onClick={() => setSelectedTheme(theme.id)}
                        title={theme.name}
                      >
                        {selectedTheme === theme.id && <Check size={14} />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Info */}
              {institutionHint && (
                <div className="logo-info">
                  {logoLoading ? (
                    <span>Finding logo for {institutionHint}...</span>
                  ) : logoDataUrl ? (
                    <span>✓ Found logo for {institutionHint}</span>
                  ) : (
                    <span>No logo found</span>
                  )}
                </div>
              )}
            </>
          )}

          {mode === 'upload' && (
            <div className="upload-section">
              {uploadPreview ? (
                <div className="upload-preview-container">
                  <img src={uploadPreview} alt="Upload preview" className="upload-preview-image" />
                  <button 
                    className="change-upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Choose Different Image
                  </button>
                </div>
              ) : (
                <label className="upload-dropzone" onClick={() => fileInputRef.current?.click()}>
                  <Upload size={32} />
                  <span>Click to upload an image</span>
                  <small>PNG, JPG, GIF up to 5MB</small>
                </label>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              {uploadError && <span className="upload-error">{uploadError}</span>}
            </div>
          )}

          {mode === 'url' && (
            <div className="custom-url-section">
              <p className="url-description">
                Paste a direct link to a card image. Right-click any card image online and select "Copy image address".
              </p>
              <label className="section-label">Image URL</label>
              <div className="url-input-group">
                <input
                  type="url"
                  value={customUrl}
                  onChange={(e) => {
                    setCustomUrl(e.target.value);
                    setUrlError('');
                  }}
                  placeholder="https://example.com/card-image.png"
                  className={urlError ? 'error' : ''}
                />
                {urlError && <span className="url-error">{urlError}</span>}
              </div>
              
              {customUrl && !urlError && (
                <div className="url-preview">
                  <img 
                    src={customUrl} 
                    alt="Preview" 
                    onError={() => setUrlError('Could not load image')}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="card-art-picker-footer">
          {currentImage && (
            <button className="btn-remove-small" onClick={handleRemoveImage}>
              Remove
            </button>
          )}
          <div className="footer-actions">
            <button className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            {mode === 'generate' && (
              <button className="btn-select" onClick={handleGenerateAndSelect}>
                Use This Design
              </button>
            )}
            {mode === 'upload' && (
              <button 
                className="btn-select" 
                onClick={handleUploadSubmit}
                disabled={!uploadPreview}
              >
                Use Image
              </button>
            )}
            {mode === 'url' && (
              <button 
                className="btn-select" 
                onClick={handleUrlSubmit}
                disabled={!customUrl.trim()}
              >
                Use URL
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * CardImagePreview - Displays a card image with hover-to-reveal actions
 * Used in PaymentAccountModal to show the current card image
 */
export function CardImagePreview({ 
  imageUrl, 
  onEdit, 
  onRemove, 
  disabled = false 
}) {
  const [showActions, setShowActions] = useState(false);

  if (!imageUrl) return null;

  return (
    <div 
      className={`card-image-preview-wrapper ${showActions ? 'actions-visible' : ''}`}
      style={{ width: '100%', maxWidth: '340px', margin: '0 auto' }}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      onClick={() => setShowActions(!showActions)} // Toggle for mobile
    >
      <div 
        className="card-image-preview"
        style={{ 
          width: '100%', 
          paddingBottom: '63.05%',
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        role="img"
        aria-label="Card preview"
      />
      <div className={`card-image-actions-overlay ${showActions ? 'visible' : ''}`}>
        <button 
          type="button"
          className="card-action-btn edit"
          onClick={(e) => {
            e.stopPropagation();
            onEdit?.();
          }}
          disabled={disabled}
          title="Change image"
        >
          <Edit2 size={16} />
        </button>
        <button 
          type="button"
          className="card-action-btn remove"
          onClick={(e) => {
            e.stopPropagation();
            onRemove?.();
          }}
          disabled={disabled}
          title="Remove image"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
}

export default CardArtPicker;
